import type ImageType from "@travily/types/shared/Image";
import { Carousel } from "@ui/components/Carousel";
import { FullBleed } from "@ui/components/FullBleed";
import { Image } from "@ui/components/Image";

export interface ProgramGalleryProps {
  images: ImageType[];
}

/**
 * Top gallery. Desktop shows the Airbnb-style hero grid — one large image with up
 * to two stacked beside it; mobile falls back to the same peeking `Carousel` the
 * product page uses. Degrades gracefully to 1–2 photos.
 */
export function ProgramGallery({ images }: ProgramGalleryProps) {
  if (images.length === 0) return null;

  const [hero, ...rest] = images;
  const secondary = rest.slice(0, 2);
  const hasSecondary = secondary.length > 0;

  return (
    <>
      {/* Mobile: peeking square carousel, breaking out of the page gutter. */}
      <FullBleed className="md:hidden">
        <Carousel aspect="square" images={images} mobile="scroll" priority sizes="100vw" />
      </FullBleed>

      {/* Desktop: 1 big + up to 2 stacked. The right column is a fixed 37% so it
          lines up with the action/message sidebar directly below it. */}
      <div className="hidden gap-5 md:flex md:h-[380px] lg:h-[480px] xl:h-[564px]">
        <div className="relative min-w-0 flex-1 overflow-hidden rounded-3xl">
          <Image
            image={hero ?? null}
            priority
            sizes="(min-width: 768px) 63vw, 100vw"
            className="h-full w-full object-cover"
          />
        </div>
        {hasSecondary && (
          <div className="flex w-[37%] shrink-0 flex-col gap-5">
            {secondary.map((image) => (
              <div key={image.src} className="relative flex-1 overflow-hidden rounded-3xl">
                <Image image={image} sizes="(min-width: 768px) 37vw, 100vw" className="h-full w-full object-cover" />
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
